/**
 * Excepciones al reparto por médico (administración, 7/10/2026).
 *
 * El reparto general («% del médico en tratamientos / en cirugías») vale para
 * todos. Una EXCEPCIÓN dice que un médico, en un tipo de acto y, si se quiere,
 * solo en un lugar (en la clínica o un hospital del catálogo de precios),
 * cobra otro % del neto desde una fecha. Gana la más concreta: la de un lugar
 * sobre la de «en cualquier sitio» y, entre iguales, la de fecha más reciente.
 *
 * Vive dentro de «Porcentajes y fungibles» (ColaboradoresPage), solo para
 * gestión. Aquí no hay ni un médico, ni un lugar, ni un porcentaje: todo viene
 * de `GET /api/colaboradores/reglas-reparto`, de los médicos colaboradores
 * posibles y de `catalogos.lugares`. El servidor lo vuelve a comprobar todo
 * (403, 409 si ya hay una igual, 422) y lo apunta en Control rastreo.
 * Retirar es blando: deja de aplicarse a los actos nuevos y lo ya registrado
 * conserva el % con que se registró.
 *
 * El % se escribe como en la clínica («45», «47,5»): `leerImporte`.
 *
 * Revisión del 7/10/2026: en el desplegable solo salen los médicos (los que
 * se apuntan actos de colaborador; el servidor no admite a nadie más); con
 * un «desde» anterior a hoy se avisa de que lo ya registrado no cambia (y,
 * con lugar, de que al reeditarlo se pedirá); y la lista solo la cambia la
 * última respuesta del servidor.
 *
 * Props: { medicos: [{id, name}], lugares: [{id, nombre}],
 *          onCambiado (tras cada cambio: recarga los catálogos de la página,
 *          que dicen a qué médicos hay que pedirles el lugar) }
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Pencil, Plus, RotateCw, Trash2 } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { dateToStr } from '../../lib/citasApi';
import { leerImporte, textoPorcentaje } from '../../lib/importeEuros';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const RUTA = '/api/colaboradores/reglas-reparto';
// Valor del desplegable para «en cualquier sitio» (no es un id de lugar).
const CUALQUIERA = '__CUALQUIER_SITIO__';
const TIPOS = [['cirugia', 'Cirugías'], ['tratamiento', 'Tratamientos']];

/** Hoy en la hora de este equipo (no la de Greenwich), «aaaa-mm-dd». */
const hoyLocal = () => dateToStr(new Date());

/** «2099-03-01» → «01/03/2099». */
function fechaLegible(iso) {
  const s = String(iso || '');
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : (s || '—');
}

/** «47,5» para la casilla (sin el símbolo). */
const pctParaCampo = (v) => (v === null || v === undefined ? '' : textoPorcentaje(v).replace(/\s%$/, ''));

function Formulario({ form, setForm, medicos, lugares, error, guardando, onGuardar, onCancelar }) {
  const pon = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // El médico o el lugar de una excepción que ya no salen en las listas
  // (usuario dado de baja, hospital retirado del catálogo) se siguen viendo.
  // Solo los que registran actos de colaborador (perfil de médico): el
  // servidor no admite una excepción para nadie más.
  const elegibles = medicos.filter((m) => !m.role || m.role === 'doctor');
  const opcionesMedico = elegibles.some((m) => m.id === form.medico_id) || !form.medico_id
    ? elegibles : [...elegibles, { id: form.medico_id, name: form.medico_nombre || form.medico_id }];
  const conLugar = form.tipo === 'cirugia' && form.lugar_id && form.lugar_id !== CUALQUIERA;
  const desdeAtras = !!form.desde && form.desde < hoyLocal();
  const opcionesLugar = !form.lugar_id || form.lugar_id === CUALQUIERA || lugares.some((l) => l.id === form.lugar_id)
    ? lugares : [...lugares, { id: form.lugar_id, nombre: `${form.lugar_nombre || form.lugar_id} (ya no está en el catálogo)` }];
  return (
    <form
      className="rounded-md border border-[rgba(46,110,94,0.35)] bg-[rgba(46,110,94,0.04)] p-3 space-y-3"
      onSubmit={(e) => { e.preventDefault(); onGuardar(); }}
      data-testid="excepcion-form"
    >
      <div className="text-sm font-semibold text-[#13231E]">
        {form.id ? 'Editar excepción' : 'Añadir excepción'}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Médico</Label>
          <Select value={form.medico_id || ''} onValueChange={(v) => pon('medico_id', v)}>
            <SelectTrigger data-testid="excepcion-form-medico"><SelectValue placeholder="Elige médico…" /></SelectTrigger>
            <SelectContent>
              {opcionesMedico.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tipo de acto</Label>
          <div className="flex gap-1.5" role="group" aria-label="Tipo de acto">
            {TIPOS.map(([k, texto]) => (
              <Button key={k} type="button" size="sm" variant={form.tipo === k ? 'default' : 'outline'}
                aria-pressed={form.tipo === k}
                className={`flex-1 ${form.tipo === k ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F]' : ''}`}
                onClick={() => pon('tipo', k)} data-testid={`excepcion-form-tipo-${k}`}>
                {texto}
              </Button>
            ))}
          </div>
        </div>
        {form.tipo === 'cirugia' ? (
          <div>
            <Label>Lugar</Label>
            <Select value={form.lugar_id || CUALQUIERA} onValueChange={(v) => pon('lugar_id', v)}>
              <SelectTrigger data-testid="excepcion-form-lugar"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={CUALQUIERA}>En cualquier sitio</SelectItem>
                {opcionesLugar.map((l) => <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <Label>Lugar</Label>
            <p className="text-sm text-muted-foreground py-2">En cualquier sitio (el lugar solo cuenta en las cirugías)</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="excepcion-pct">% del médico</Label>
            <Input id="excepcion-pct" value={form.pct} onChange={(e) => pon('pct', e.target.value)}
              inputMode="decimal" placeholder="Ej.: 47,5" autoComplete="off" className="text-right tabular-nums"
              data-testid="excepcion-form-pct" />
          </div>
          <div>
            <Label htmlFor="excepcion-desde">Desde</Label>
            <Input id="excepcion-desde" type="date" value={form.desde} onChange={(e) => pon('desde', e.target.value)}
              data-testid="excepcion-form-desde" />
          </div>
        </div>
      </div>
      <div>
        <Label htmlFor="excepcion-nota">Nota <span className="text-muted-foreground text-xs">(opcional)</span></Label>
        <Input id="excepcion-nota" value={form.nota} onChange={(e) => pon('nota', e.target.value)} maxLength={300}
          autoComplete="off" data-testid="excepcion-form-nota" />
      </div>
      {desdeAtras && (
        <p className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5"
          data-testid="excepcion-form-aviso-pasado">
          Esa fecha es anterior a hoy: los actos ya registrados en ese periodo no cambian
          {conLugar ? ', pero al reeditarlos pedirán el lugar.' : '.'}
        </p>
      )}
      {conLugar && (
        <p className="text-xs text-muted-foreground" data-testid="excepcion-form-aviso-lugar">
          Solo en las cirugías hechas en ese lugar; en cualquier otro sitio, el reparto general. A partir de esa
          fecha, las cirugías de este médico tendrán que decir dónde se ha operado.
        </p>
      )}
      {error && <p className="text-sm text-red-700" role="alert" data-testid="excepcion-form-error">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancelar} disabled={guardando}>Cancelar</Button>
        <Button type="submit" disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
          data-testid="excepcion-form-guardar">
          {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar excepción
        </Button>
      </div>
    </form>
  );
}

export default function ExcepcionesReparto({ medicos = [], lugares = [], onCambiado = null }) {
  const [reglas, setReglas] = useState(null);
  const [estado, setEstado] = useState('cargando');     // 'cargando' | 'listo' | 'error'
  const [form, setForm] = useState(null);
  const [errorForm, setErrorForm] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(null);   // id de la que se va a retirar
  const [retirando, setRetirando] = useState(false);

  // Solo cuenta la ÚLTIMA petición (como `cargarCatalogos` de la página): si
  // una recarga lenta llega después de otra más nueva, no pisa la lista.
  const turno = useRef(0);

  /** Carga la lista; true si ha podido. */
  const cargar = useCallback(() => {
    turno.current += 1;
    const mio = turno.current;
    setEstado('cargando');
    return api.get(RUTA)
      .then((r) => {
        if (turno.current !== mio) return true;
        setReglas(Array.isArray(r.data?.reglas) ? r.data.reglas : []);
        setEstado('listo');
        return true;
      })
      .catch(() => {
        if (turno.current === mio) setEstado('error');
        return false;
      });
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const despuesDeCambiar = async () => {
    const ok = await cargar();
    onCambiado?.();
    if (!ok) toast.error('Guardado, pero no se ha podido recargar la lista: pulsa «Reintentar».');
  };
  const yaNoEsta = async () => {
    setForm(null); setConfirmando(null);
    await cargar();
    onCambiado?.();
    toast.error('Esa excepción ya no está: la ha retirado otra persona. Se ha recargado la lista.');
  };

  const abrirNueva = () => {
    setConfirmando(null); setErrorForm('');
    setForm({ medico_id: '', tipo: 'cirugia', lugar_id: CUALQUIERA, pct: '', desde: hoyLocal(), nota: '' });
  };
  const abrirEditar = (r) => {
    setConfirmando(null); setErrorForm('');
    setForm({
      id: r.id, medico_id: r.medico_id || '', medico_nombre: r.medico_nombre, tipo: r.tipo || 'cirugia',
      lugar_id: r.lugar_id || CUALQUIERA, lugar_nombre: r.lugar_nombre,
      pct: pctParaCampo(r.pct_medico), desde: r.desde || hoyLocal(), nota: r.nota || '',
    });
  };

  const guardar = async () => {
    if (!form.medico_id) { setErrorForm('Elige el médico.'); return; }
    const pct = leerImporte(form.pct);
    if (pct === null || Number.isNaN(pct)) { setErrorForm('Escribe el porcentaje del médico, por ejemplo 45 o 47,5.'); return; }
    if (pct > 100) { setErrorForm('El porcentaje va de 0 a 100.'); return; }
    if (!form.desde) { setErrorForm('Indica desde qué fecha cuenta.'); return; }
    const cuerpo = {
      medico_id: form.medico_id,
      tipo: form.tipo,
      lugar_id: form.tipo === 'cirugia' && form.lugar_id && form.lugar_id !== CUALQUIERA ? form.lugar_id : null,
      pct_medico: pct,
      desde: form.desde,
      nota: (form.nota || '').trim(),
    };
    setGuardando(true); setErrorForm('');
    try {
      if (form.id) await api.put(`${RUTA}/${form.id}`, cuerpo);
      else await api.post(RUTA, cuerpo);
      toast.success(form.id ? 'Cambios guardados' : 'Excepción añadida');
      setForm(null);
      await despuesDeCambiar();
    } catch (e) {
      if (form.id && e?.response?.status === 404) await yaNoEsta();
      else setErrorForm(mensajeError(e, 'No se ha podido guardar'));
    } finally {
      setGuardando(false);
    }
  };

  const retirar = async (r) => {
    setRetirando(true);
    try {
      await api.delete(`${RUTA}/${r.id}`);
      toast.success('Excepción retirada');
      setConfirmando(null);
      if (form?.id === r.id) setForm(null);
      await despuesDeCambiar();
    } catch (e) {
      if (e?.response?.status === 404) await yaNoEsta();
      else toast.error(mensajeError(e, 'No se ha podido retirar'));
    } finally {
      setRetirando(false);
    }
  };

  const lista = reglas || [];
  return (
    <section className="space-y-3" data-testid="excepciones-reparto">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-[#13231E]">Excepciones por médico</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Un médico que cobra otro porcentaje en un tipo de acto (y, si quieres, solo en un lugar) a partir de una
            fecha. Gana la más concreta: la de un lugar sobre la de «en cualquier sitio» y, entre iguales, la más
            reciente. Lo ya registrado conserva su porcentaje.
          </p>
        </div>
        {!form && reglas && (
          <Button type="button" size="sm" onClick={abrirNueva} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="excepcion-anadir">
            <Plus className="w-4 h-4 mr-1" /> Añadir excepción
          </Button>
        )}
      </div>

      {estado === 'error' && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-900"
          role="alert" data-testid="excepciones-error">
          <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
          <p className="flex-1 min-w-[12rem] text-sm">
            {reglas ? 'No se ha podido recargar la lista: lo que ves puede no estar al día.'
              : 'No se han podido cargar las excepciones. Comprueba la conexión y vuelve a intentarlo.'}
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => cargar()} data-testid="excepciones-reintentar">
            <RotateCw className="w-4 h-4 mr-1.5" /> Reintentar
          </Button>
        </div>
      )}

      {form && (
        <Formulario form={form} setForm={setForm} medicos={medicos} lugares={lugares} error={errorForm}
          guardando={guardando} onGuardar={guardar} onCancelar={() => { setForm(null); setErrorForm(''); }} />
      )}

      {!reglas && estado !== 'error' && (
        <p className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground" role="status"
          data-testid="excepciones-cargando">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Cargando…
        </p>
      )}

      {reglas && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="excepciones-tabla">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3 font-semibold">Médico</th>
                <th className="py-2 px-3 font-semibold">Tipo</th>
                <th className="py-2 px-3 font-semibold">Lugar</th>
                <th className="py-2 px-3 font-semibold text-right">%</th>
                <th className="py-2 px-3 font-semibold whitespace-nowrap">Desde</th>
                <th className="py-2 pl-3 w-px" />
              </tr>
            </thead>
            <tbody>
              {lista.map((r) => (confirmando === r.id ? (
                <tr key={r.id} className="border-b border-border/60" data-testid="excepcion-fila">
                  <td colSpan={6} className="py-2">
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950"
                      role="alertdialog" aria-label="Retirar excepción" data-testid="excepcion-confirmar">
                      <p className="text-sm">
                        <span className="font-semibold">¿Retirar la excepción de {r.medico_nombre || 'este médico'}?</span>{' '}
                        Dejará de aplicarse a los actos nuevos, que irán con el reparto general. Lo ya registrado
                        conserva su porcentaje.
                      </p>
                      <div className="mt-2 flex flex-wrap justify-end gap-2">
                        <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmando(null)}
                          disabled={retirando}>Cancelar</Button>
                        <Button type="button" size="sm" variant="destructive" onClick={() => retirar(r)}
                          disabled={retirando} data-testid="excepcion-confirmar-retirar">
                          {retirando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Retirar
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={r.id} className="border-b border-border/60 align-top" data-testid="excepcion-fila">
                  <td className="py-2 pr-3">
                    <div className="font-medium text-[#13231E]" data-testid="excepcion-fila-medico">{r.medico_nombre || '—'}</div>
                    {r.nota && <div className="text-xs text-muted-foreground">{r.nota}</div>}
                  </td>
                  <td className="py-2 px-3 whitespace-nowrap">{r.tipo === 'tratamiento' ? 'Tratamientos' : 'Cirugías'}</td>
                  <td className="py-2 px-3" data-testid="excepcion-fila-lugar">
                    {r.lugar_id ? r.lugar_nombre : 'En cualquier sitio'}
                    {r.lugar_id && r.lugar_vigente === false && (
                      <div className="text-xs text-amber-800">Ya no está en el catálogo de precios</div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap font-semibold" data-testid="excepcion-fila-pct">
                    {textoPorcentaje(r.pct_medico)}
                  </td>
                  <td className="py-2 px-3 tabular-nums whitespace-nowrap" data-testid="excepcion-fila-desde">{fechaLegible(r.desde)}</td>
                  <td className="py-1 pl-3">
                    <div className="flex justify-end gap-0.5">
                      <Button type="button" size="sm" variant="ghost" onClick={() => abrirEditar(r)}
                        title="Editar" aria-label={`Editar la excepción de ${r.medico_nombre || 'este médico'}`}
                        data-testid="excepcion-editar">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button type="button" size="sm" variant="ghost" className="text-destructive"
                        onClick={() => setConfirmando(r.id)} title="Retirar"
                        aria-label={`Retirar la excepción de ${r.medico_nombre || 'este médico'}`}
                        data-testid="excepcion-retirar">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )))}
              {lista.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-5 text-center text-sm text-muted-foreground" data-testid="excepciones-vacio">
                    Sin excepciones: todos los médicos cobran el reparto general.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
